import { TerminalAttachmentError } from '../../../enums/terminal';
import {
  TERMINAL_ATTACHMENT_ALLOWED_TYPES,
  TERMINAL_ATTACHMENT_EXTENSION_TYPES,
  TERMINAL_ATTACHMENT_IMAGE_PREFIX,
  TERMINAL_ATTACHMENT_MAX_BYTES,
  TERMINAL_ATTACHMENT_MAX_PER_MESSAGE,
} from '../../../utils/constants/terminalConstants';

const BYTES_PER_KB = 1024;
const KB_PER_MB = 1024;
const EXTENSION_SEPARATOR = '.';

export function resolveContentType(file: Pick<File, 'name' | 'type'>): string {
  if (file.type) {
    return file.type;
  }
  const extension =
    file.name.split(EXTENSION_SEPARATOR).pop()?.toLowerCase() ?? '';
  return TERMINAL_ATTACHMENT_EXTENSION_TYPES[extension] ?? '';
}

export function validateAttachment(
  file: Pick<File, 'name' | 'type' | 'size'>,
): TerminalAttachmentError | null {
  if (file.size === 0) {
    return TerminalAttachmentError.EMPTY;
  }
  if (file.size > TERMINAL_ATTACHMENT_MAX_BYTES) {
    return TerminalAttachmentError.TOO_LARGE;
  }
  return TERMINAL_ATTACHMENT_ALLOWED_TYPES.has(resolveContentType(file))
    ? null
    : TerminalAttachmentError.TYPE_NOT_ALLOWED;
}

export function partitionAttachments(
  files: File[],
  existingCount: number,
): { accepted: File[]; error: TerminalAttachmentError | null } {
  const accepted: File[] = [];
  let error: TerminalAttachmentError | null = null;
  for (const file of files) {
    const problem = validateAttachment(file);
    if (problem) {
      error = problem;
    } else if (
      existingCount + accepted.length >=
      TERMINAL_ATTACHMENT_MAX_PER_MESSAGE
    ) {
      error = TerminalAttachmentError.TOO_MANY;
    } else {
      accepted.push(file);
    }
  }
  return { accepted, error };
}

export function isImageFile(file: Pick<File, 'name' | 'type'>): boolean {
  return resolveContentType(file).startsWith(TERMINAL_ATTACHMENT_IMAGE_PREFIX);
}

export function formatBytes(size: number): string {
  if (size < BYTES_PER_KB) {
    return `${size} B`;
  }
  const kb = size / BYTES_PER_KB;
  return kb < KB_PER_MB
    ? `${Math.round(kb)} KB`
    : `${(kb / KB_PER_MB).toFixed(1)} MB`;
}
